(() => {
  if (window.OPSFontLibrary) return;

  const DB_NAME = 'ops-font-library';
  const STORE_NAME = 'fonts';
  const DB_VERSION = 1;
  const MAX_FILE_SIZE = 12 * 1024 * 1024;
  const EXTENSIONS = ['.ttf', '.otf', '.woff', '.woff2'];
  const BUILTIN_FONTS = [
    { value: "'DM Sans', Arial, sans-serif", label: 'DM Sans · clean' },
    { value: "'League Spartan', Arial, sans-serif", label: 'League Spartan · bold' },
    { value: "'Bebas Neue', Impact, sans-serif", label: 'Bebas Neue · condensed' },
    { value: "'Fredoka', Arial, sans-serif", label: 'Fredoka · playful' },
    { value: "'Playfair Display', Georgia, serif", label: 'Playfair Display · editorial' },
    { value: "'Cinzel', Georgia, serif", label: 'Cinzel · classical' },
    { value: "'Caveat', cursive", label: 'Caveat · handwritten' },
    { value: "'Bangers', Impact, sans-serif", label: 'Bangers · comic display' },
    { value: "'Space Mono', 'Courier New', monospace", label: 'Space Mono · typewriter' },
    { value: 'Arial, sans-serif', label: 'Arial · system sans' },
    { value: 'Georgia, serif', label: 'Georgia · system serif' }
  ];

  const uid = () => `font_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  const displayName = (fileName) => String(fileName || 'Uploaded font')
    .replace(/\.(ttf|otf|woff2?)$/i, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const internalFamily = (id) => `OPSUser_${String(id).replace(/[^a-z0-9_]/gi, '_')}`;
  const customFamilyFromValue = (value) => String(value || '').match(/OPSUser_[A-Za-z0-9_]+/)?.[0] || '';
  const isCustomValue = (value) => Boolean(customFamilyFromValue(value));

  let dbPromise = null;
  const loaded = new Map();

  const openDb = () => {
    if (dbPromise) return dbPromise;
    dbPromise = new Promise((resolve, reject) => {
      if (!('indexedDB' in window)) return reject(new Error('Browser font storage is not supported on this device.'));
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(STORE_NAME)) {
          request.result.createObjectStore(STORE_NAME, { keyPath: 'id' });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error || new Error('Font storage could not be opened.'));
    });
    return dbPromise;
  };

  const transact = async (mode, callback) => {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, mode);
      const store = tx.objectStore(STORE_NAME);
      let request;
      try { request = callback(store); } catch (error) { reject(error); return; }
      tx.oncomplete = () => resolve(request?.result);
      tx.onerror = () => reject(tx.error || request?.error || new Error('Font storage operation failed.'));
      tx.onabort = () => reject(tx.error || new Error('Font storage operation was cancelled.'));
    });
  };

  const validateFile = (file) => {
    if (!(file instanceof Blob)) return 'Choose a font file from your device.';
    const name = String(file.name || '').toLowerCase();
    const extension = EXTENSIONS.find((item) => name.endsWith(item));
    if (!extension) return 'Choose a TTF, OTF, WOFF or WOFF2 font file.';
    if (file.size > MAX_FILE_SIZE) return 'That font is larger than 12 MB.';
    if (!('FontFace' in window) || !document.fonts) return 'Custom fonts are not supported by this browser.';
    return '';
  };

  const loadRecord = async (record) => {
    if (!record?.id || !(record.file instanceof Blob)) throw new Error('The saved font file is unavailable.');
    if (loaded.has(record.id)) return loaded.get(record.id);
    if (!('FontFace' in window) || !document.fonts) throw new Error('Custom fonts are not supported by this browser.');
    const family = record.family || internalFamily(record.id);
    const buffer = await record.file.arrayBuffer();
    const face = new FontFace(family, buffer);
    await face.load();
    document.fonts.add(face);
    const result = { ...record, family, cssValue: `'${family}'` };
    loaded.set(record.id, result);
    return result;
  };

  const list = async ({ load = false } = {}) => {
    const records = await transact('readonly', (store) => store.getAll()).catch(() => []);
    records.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
    if (!load) return records;
    const results = [];
    for (const record of records) {
      try { results.push(await loadRecord(record)); } catch (error) { results.push({ ...record, loadError: error.message }); }
    }
    return results;
  };

  const ensureValueAvailable = async (value) => {
    const family = customFamilyFromValue(value);
    if (!family) return true;
    const loadedRecord = [...loaded.values()].find((record) => record.family === family);
    if (loadedRecord) return true;
    const records = await list({ load: false });
    const record = records.find((item) => (item.family || internalFamily(item.id)) === family);
    if (!record) throw new Error('A customer font used in this design is no longer saved in My Fonts. Upload that font again before exporting or ordering.');
    await loadRecord(record);
    return true;
  };

  const ensureValuesAvailable = async (values) => {
    for (const value of [...new Set(Array.from(values || []).filter(Boolean))]) await ensureValueAvailable(value);
    return true;
  };

  const saveFiles = async (files) => {
    const saved = [];
    const errors = [];
    for (const file of Array.from(files || [])) {
      const validation = validateFile(file);
      if (validation) { errors.push(`${file?.name || 'Font'}: ${validation}`); continue; }
      const id = uid();
      const now = new Date().toISOString();
      const record = {
        id,
        kind: 'font',
        name: displayName(file.name),
        fileName: file.name,
        type: file.type || 'application/octet-stream',
        size: file.size,
        family: internalFamily(id),
        file,
        createdAt: now,
        updatedAt: now
      };
      try {
        await loadRecord(record);
        await transact('readwrite', (store) => store.put(record));
        saved.push(record);
      } catch (error) {
        loaded.delete(id);
        errors.push(`${file.name}: ${error.message || 'This font could not be loaded.'}`);
      }
    }
    if (saved.length) window.dispatchEvent(new CustomEvent('ops:font-library-updated', { detail: { action: 'save', count: saved.length } }));
    return { saved, errors };
  };

  const rename = async (id, name) => {
    const record = await transact('readonly', (store) => store.get(id));
    if (!record) throw new Error('That font could not be found.');
    record.name = String(name || '').trim() || record.name;
    record.updatedAt = new Date().toISOString();
    await transact('readwrite', (store) => store.put(record));
    window.dispatchEvent(new CustomEvent('ops:font-library-updated', { detail: { action: 'rename', id } }));
    return record;
  };

  const remove = async (id) => {
    await transact('readwrite', (store) => store.delete(id));
    const loadedRecord = loaded.get(id);
    if (loadedRecord?.family && document.fonts) {
      for (const face of document.fonts) {
        if (face.family === loadedRecord.family) document.fonts.delete(face);
      }
    }
    loaded.delete(id);
    window.dispatchEvent(new CustomEvent('ops:font-library-updated', { detail: { action: 'delete', id } }));
  };

  const appendOptions = async (select, selectedValue = '') => {
    if (!select) return;
    select.querySelectorAll('optgroup[data-ops-user-fonts]').forEach((node) => node.remove());
    const records = await list({ load: true });
    const valid = records.filter((record) => !record.loadError);
    if (valid.length) {
      const group = document.createElement('optgroup');
      group.label = 'My Fonts';
      group.dataset.opsUserFonts = 'true';
      valid.forEach((record) => {
        const option = new Option(record.name || record.fileName || 'My font', record.cssValue || `'${record.family}'`);
        option.dataset.fontId = record.id;
        group.append(option);
      });
      select.append(group);
    }
    if (selectedValue && [...select.options].some((option) => option.value === selectedValue)) select.value = selectedValue;
  };

  const refreshSelects = async (scope = document) => {
    const selects = [...scope.querySelectorAll('[data-font], [data-object-font]')];
    await Promise.all(selects.map((select) => appendOptions(select, select.value)));
  };

  window.OPSFontLibrary = {
    DB_NAME,
    STORE_NAME,
    BUILTIN_FONTS,
    validateFile,
    list,
    saveFiles,
    rename,
    remove,
    loadRecord,
    isCustomValue,
    ensureValueAvailable,
    ensureValuesAvailable,
    appendOptions,
    refreshSelects,
    openDb,
    transact
  };
})();
